import { useEffect, useRef } from "react";
import { removeSentence, removeVerb, type WorkoutUsage } from "../lib/archiveWorkout";

/**
 * DELETE'S CONFIRM — THE ONE SENTENCE AND THE ONE PAIR OF BUTTONS, for every screen that deletes a workout.
 *
 * 🔴 W85 (his report, 2026-09-27): *"don't we need a delete in the workouts at least in the 3 dots extra choices??"* —
 * the builder's `⋯` gains the Delete the overview window has carried since W17d. It is the SAME act: the sentence and the
 * verb are `archiveWorkout.ts`'s (`removeSentence` / `removeVerb` — the branch said out loud, Delete or Archive, never
 * "are you sure"), the act is `removeWorkout` over `removeDeps`, and this group is what both screens draw. A second copy
 * of these three lines is where one screen would one day say "Delete" over an archive.
 *
 * ⚠️ TWO FRAMES, ONE CONTENT, AND THE DIFFERENCE IS STATED. The overview window draws the group IN ITS FOOTER, because
 * that question is about the workout drawn above it and a second modal would grey it out. The builder's question is about
 * the same workout but the builder is the screen holding the coach's edits, and its footer is the undo row — so there the
 * group sits in a small centred window (`RemoveWorkoutWindow`), the frame `DiscardWindow` already gives a builder question.
 */
export function RemoveConfirm({ name, usage, working, onCancel, onConfirm, cancelRef }: {
  readonly name: string;
  readonly usage: WorkoutUsage;
  /** The act is running: both buttons hold, and the verb says so. */
  readonly working: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  /** Where focus lands when the question opens in a window (the safe answer). */
  readonly cancelRef?: React.Ref<HTMLButtonElement>;
}) {
  const verb = removeVerb(usage);
  return (
    <div className="overview-confirm" role="group" aria-label={verb + " this workout"}>
      <p className="overview-confirm-sentence">{removeSentence(name, usage)}</p>
      <div className="overview-actions">
        <button type="button" className="ht-button" ref={cancelRef} disabled={working}
                onClick={onCancel}>Cancel</button>
        {/* The destructive tier: an outlined button in the error ink, never a filled red one — §13's ladder has no fill
            at any tier and the accent is the screen's primary. */}
        <button type="button" className="ht-button ht-button-danger overview-remove-go" disabled={working}
                onClick={onConfirm}>
          {working ? `${verb}…` : verb}
        </button>
      </div>
    </div>
  );
}

/**
 * The builder's frame for the same question: a centred modal over the dimmed builder (the `ht-confirm` window
 * `DiscardWindow` draws), a native `<dialog>` so Escape and the focus trap are the browser's. Escape = Cancel.
 */
export function RemoveWorkoutWindow({ open, name, usage, working, error, onCancel, onConfirm }: {
  readonly open: boolean;
  readonly name: string;
  /** `null` while the counts are being read — the sentence cannot be written until they are. */
  readonly usage: WorkoutUsage | null;
  readonly working: boolean;
  /** A failed read or a failed act, in the words `removeWorkout` wrote. */
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  useEffect(() => { if (open) cancelRef.current?.focus(); }, [open, usage]);
  return (
    <dialog ref={dialogRef} className="ht-window ht-confirm" aria-labelledby="remove-workout-title"
            onCancel={(e) => { e.preventDefault(); if (!working) onCancel(); }}>
      <div className="ht-window-frame ht-confirm-frame">
        <h2 className="ht-title ht-confirm-title" id="remove-workout-title">
          {usage === null ? "Checking…" : `${removeVerb(usage)} workout`}
        </h2>
        {error !== null && <p className="ht-callout overview-error" role="alert">{error}</p>}
        {usage === null && error === null && (
          <p className="ht-confirm-sentence" aria-live="polite">Checking what uses “{name}”…</p>
        )}
        {usage !== null && (
          <RemoveConfirm name={name} usage={usage} working={working} cancelRef={cancelRef}
                         onCancel={onCancel} onConfirm={onConfirm} />
        )}
        {usage === null && error !== null && (
          <div className="ht-confirm-actions">
            <button type="button" ref={cancelRef} className="ht-button" onClick={onCancel}>Close</button>
          </div>
        )}
      </div>
    </dialog>
  );
}
